import React, { useContext, ReactElement, useCallback } from "react";
import { FinancieringOptions } from "../../.generated/forms/formstypes";
import { Button } from "react-bootstrap";
import { DashboardDataContext, NewDashboardType } from "./new-dashboard-context";
import { SnelInzichtResultaat } from "./snel-inzicht/snel-inzicht";
import { HypotheekAcceptatieResultaat } from "./hypotheek-acceptatie/hypotheek-acceptatie";
import { ArbeidsongeschiktheidResultaat } from "./arbeidsongeschiktheid/arbeidsongeschiktheid";
import { OverlijdenResultaat } from "./overlijden/overlijden";
import { WerkloosheidResultaat } from "./werkloosheid/werkloosheid";
import { PensioenResultaat } from "./pensioen/pensioen";
import { useRefreshTrigger } from "./infra/use-refresh-trigger";
import { useRequestInit, FeatureComponent } from "adviesbox-shared";
import { useMenuInfoData } from "../../shared/hooks/use-menu-info-data";

import { AppDataContext } from "../../navigation/appdata-context";
import { cleanCache } from "./infra/scenario-cache";
import DashboardProvider from "../infra/dashboard-provider";
import DashboardContext from "../infra/dashboard-context";
import classNames from "classnames";
import "./new-dashboard.scss";

const DashboardContent = ({
  situatie,
  onClickOnderwerp
}: {
  situatie: string;
  onClickOnderwerp?: (type: NewDashboardType) => void;
}): ReactElement => {
  const { open, type, setOpen, setType } = useContext(DashboardDataContext);
  const { reloadCount } = useContext(AppDataContext);
  const { data: menuInfo } = useMenuInfoData(reloadCount);
  const { isPageDirty } = useContext(AppDataContext);
  const { dashboardValues } = useContext(DashboardContext);
  const { triggerRefresh } = useRefreshTrigger();
  const { params, settings, user } = useRequestInit();
 
  const handleRefresh = async (): Promise<void> => {
    user && (await cleanCache(user, params, settings));
    triggerRefresh();
  };

  const handleClickOnderwerp = useCallback(
    (newType: NewDashboardType): void => {
      if (onClickOnderwerp) {
        onClickOnderwerp(newType);
        return;
      }

      if (open && type === newType) {
        setOpen(false);
        return;
      }

      if (open && type !== NewDashboardType.Geen) {
        setType(newType);
        return;
      }

      setType(newType);
      setOpen(!open);
    },
    [onClickOnderwerp, open, type, setOpen, setType]
  );

  const isSelected = (newType: NewDashboardType): boolean => {
    if (open && newType === type) return true;
    return false;
  };

  if (!dashboardValues) {
    return <div>Loading...</div>;
  }

  return (
    <div className="new-dashboard">
      <div className="new-dashboard__content">
        <div className="new-dashboard__title">
          <h1>Dashboard</h1>
          <Button
            name="Berekenen"
            size="lg"
            className="new-dashboard__button"
            onClick={handleRefresh}
            disabled={!!isPageDirty}
          >
            Berekenen
          </Button>
        </div>
        <div
          className={classNames("new-dashboard__entry", {
            "new-dashboard__entry--selected": isSelected(NewDashboardType.SnelInzicht)
          })}
        >
          <SnelInzichtResultaat
            onClick={() => handleClickOnderwerp(NewDashboardType.SnelInzicht)}
            situatie={situatie}
          />
        </div>
        {situatie === "voorstel" && menuInfo && menuInfo.financiering !== FinancieringOptions.GeenFinanciering && (
          <div
            className={classNames("new-dashboard__entry", {
              "new-dashboard__entry--selected": isSelected(NewDashboardType.HypotheekAcceptatie)
            })}
          >
            <HypotheekAcceptatieResultaat
              onClick={() => handleClickOnderwerp(NewDashboardType.HypotheekAcceptatie)}
              situatie={situatie}
            />
          </div>
        )}
        <FeatureComponent feature="FeatureNewDashboardScenarios">
          {dashboardValues.formikValues.overlijdenWeergeven && (
            <div
              className={classNames("new-dashboard__entry", {
                "new-dashboard__entry--selected": isSelected(NewDashboardType.Overlijden)
              })}
            >
              <OverlijdenResultaat
                onClick={() => handleClickOnderwerp(NewDashboardType.Overlijden)}
                situatie={situatie}
              />
            </div>
          )}
          {dashboardValues.formikValues.arbeidsongeschiktheidWeergeven && (
            <div
              className={classNames("new-dashboard__entry", {
                "new-dashboard__entry--selected": isSelected(NewDashboardType.Arbeidsongeschiktheid)
              })}
            >
              <ArbeidsongeschiktheidResultaat
                onClick={() => handleClickOnderwerp(NewDashboardType.Arbeidsongeschiktheid)}
                situatie={situatie}
              />
            </div>
          )}
          {dashboardValues.formikValues.werkloosheidWeergeven && (
            <div
              className={classNames("new-dashboard__entry", {
                "new-dashboard__entry--selected": isSelected(NewDashboardType.Werkloosheid)
              })}
            >
              <WerkloosheidResultaat
                onClick={() => handleClickOnderwerp(NewDashboardType.Werkloosheid)}
                situatie={situatie}
              />
            </div>
          )}
          {dashboardValues.formikValues.pensioenWeergeven && (
            <div
              className={classNames("new-dashboard__entry", {
                "new-dashboard__entry--selected": isSelected(NewDashboardType.Pensioen)
              })}
            >
              <PensioenResultaat onClick={() => handleClickOnderwerp(NewDashboardType.Pensioen)} situatie={situatie} />
            </div>
          )}
        </FeatureComponent>
      </div>
    </div>
  );
};

export const NewDashboardComponent = ({
  situatie,
  onClickOnderwerp
}: {
  situatie: string;
  onClickOnderwerp?: (type: NewDashboardType) => void;
}): ReactElement => {
  const { params } = useRequestInit();

  return (
    <DashboardProvider situatie={params.voorstel ? "voorstel" : "huidig"}>
      <DashboardContent situatie={situatie} onClickOnderwerp={onClickOnderwerp} />
    </DashboardProvider>
  );
};

export default NewDashboardComponent;
